import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FiBarChart2, FiCheckCircle, FiEye, FiFileText, FiLoader, FiMessageSquare, FiRefreshCw, FiX} from 'react-icons/fi';
import {examFeedbackApi, getErrorMessage} from '../services/api';
import FeedbackDetails from './FeedbackDetails';
import {feedbackCategoryLabels, feedbackScopeLabels, formatFeedbackDate, toFeedbackPage} from './examFeedbackUtils';

const snapshotSummary = (item) => {
    const snapshot = item.snapshot || {};
    const answer = snapshot.answer;
    return {
        examTitle: snapshot.exam_title || `آزمون ${item.exam_id}`,
        attempt: snapshot.attempt_number,
        question: answer?.position,
        awardedPoints: answer?.awarded_points,
        questionPoints: answer?.question_points,
        gradingType: answer?.grading_type,
        prompt: answer?.prompt,
    };
};

export default function TeacherFeedbackAnalytics({examId, onError}) {
    const [scope, setScope] = useState('');
    const [category, setCategory] = useState('');
    const [page, setPage] = useState(1);
    const [feedback, setFeedback] = useState(toFeedbackPage());
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const lastTriggerRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [items, summary] = await Promise.all([
                examFeedbackApi.master.feedback({exam_id: examId, ...(scope ? {scope} : {}), ...(category ? {category} : {}), page, page_size: 20}),
                examFeedbackApi.master.analytics({exam_id: examId}),
            ]);
            setFeedback(toFeedbackPage(items));
            setAnalytics(summary);
        } catch (error) { onError(getErrorMessage(error)); }
        finally { setLoading(false); }
    }, [examId, scope, category, page, onError]);

    const closeDetails = useCallback(() => {
        setSelectedFeedback(null);
        window.setTimeout(() => lastTriggerRef.current?.focus(), 0);
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!selectedFeedback) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const closeOnEscape = (event) => event.key === 'Escape' && closeDetails();
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [selectedFeedback, closeDetails]);

    const openDetails = (item, trigger) => {
        lastTriggerRef.current = trigger;
        setSelectedFeedback(item);
    };
    return <div className="ef-analytics">
        <div className="tew-metrics">
            <article><span><FiMessageSquare/></span><div><strong>{analytics?.feedback_count ?? '—'}</strong><small>بازخورد این آزمون</small></div></article>
            <article><span><FiBarChart2/></span><div><strong>{analytics?.appeal_count ?? '—'}</strong><small>اعتراض همه آزمون‌های قابل مدیریت</small></div></article>
            <article><span><FiBarChart2/></span><div><strong>{analytics?.accepted_appeal_count ?? '—'}</strong><small>اعتراض پذیرفته‌شده در همه آزمون‌ها</small></div></article>
            <article><span><FiBarChart2/></span><div><strong>{analytics?.appeal_acceptance_rate == null ? '—' : `${analytics.appeal_acceptance_rate}٪`}</strong><small>نرخ پذیرش در همه آزمون‌ها</small></div></article>
        </div>
        <div className="ef-scope-warning">در نسخه فعلی API، شاخص‌های اعتراض به همه آزمون‌های قابل مدیریت استاد مربوط‌اند و فقط آمار بازخورد با آزمون جاری محدود می‌شود.</div>

        <section className="tew-panel">
            <header><div><h2>گروه‌های بازخورد</h2><p>توزیع موضوع و میانگین امتیاز بازخوردهای آزمون</p></div></header>
            <div className="ef-group-grid">{(analytics?.feedback_groups || []).map((group, index) => <article key={`${group.scope}-${group.category}-${index}`}><span>{feedbackScopeLabels[group.scope] || group.scope}</span><strong>{feedbackCategoryLabels[group.category] || group.category}</strong><p>{group.count} مورد · میانگین {group.average_rating ?? '—'} از ۵</p></article>)}{!analytics?.feedback_groups?.length && <div className="tew-state">داده‌ای برای تحلیل وجود ندارد.</div>}</div>
        </section>

        <section className="tew-panel">
            <header><div><h2>بازخوردهای دانشجویان</h2><p>خلاصه زمینه هر بازخورد را ببینید و برای مشاهده snapshot کامل وارد جزئیات شوید.</p></div><button className="tew-secondary" onClick={load}><FiRefreshCw className={loading ? 'spin' : ''}/></button></header>
            <div className="tew-table-tools"><select value={scope} onChange={(event) => { setScope(event.target.value); setPage(1); }}><option value="">همه حوزه‌ها</option>{Object.entries(feedbackScopeLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select><select value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }}><option value="">همه موضوع‌ها</option>{Object.entries(feedbackCategoryLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></div>
            {loading ? <div className="tew-state"><FiLoader className="spin"/></div> : <div className="ef-feedback-list">{feedback.results.map((item) => {
                const context = snapshotSummary(item);
                return <article key={item.id}>
                    <header><div><strong>{item.student?.full_name || 'دانشجو'}</strong><small>{item.student?.email}</small></div><span>{item.rating ?? '—'} از ۵</span></header>
                    <div className="ef-feedback-card-context"><strong><FiFileText/> {context.examTitle}</strong><span>تلاش {context.attempt ?? '—'}{context.question ? ` · سؤال ${context.question}` : ''}</span>{context.awardedPoints != null && <span>نمره پاسخ: {context.awardedPoints} از {context.questionPoints ?? '—'}</span>}</div>
                    {context.prompt && <p className="ef-feedback-prompt">{context.prompt}</p>}
                    <p className="ef-feedback-card-comment">{item.comment || 'بدون توضیح'}</p>
                    <div className="ef-feedback-card-tags"><span>{feedbackScopeLabels[item.scope] || item.scope}</span><span>{feedbackCategoryLabels[item.category] || item.category}</span>{context.gradingType && <span>{context.gradingType === 'manual' ? 'تصحیح دستی' : 'تصحیح خودکار'}</span>}{item.consent_for_improvement && <span className="consent"><FiCheckCircle/> مجاز برای بهبود</span>}</div>
                    <footer><time>{formatFeedbackDate(item.created_at)}</time><button className="tew-secondary" onClick={(event) => openDetails(item, event.currentTarget)}><FiEye/> مشاهده جزئیات</button></footer>
                </article>;
            })}{!feedback.results.length && <div className="tew-state">بازخوردی با این شرایط ثبت نشده است.</div>}</div>}
            {Math.ceil((feedback.count || 0) / 20) > 1 && <div className="tew-pagination"><button disabled={!feedback.previous} onClick={() => setPage((current) => current - 1)}>قبلی</button><span>{page}</span><button disabled={!feedback.next} onClick={() => setPage((current) => current + 1)}>بعدی</button></div>}
        </section>

        {selectedFeedback && <div className="ef-drawer-overlay" onClick={closeDetails}><aside className="ef-feedback-drawer" role="dialog" aria-modal="true" aria-labelledby="teacher-feedback-detail-title" onClick={(event) => event.stopPropagation()}><header><div><span><FiMessageSquare/></span><div><h2 id="teacher-feedback-detail-title">جزئیات بازخورد</h2><p>{selectedFeedback.snapshot?.exam_title || `آزمون ${selectedFeedback.exam_id}`}</p></div></div><button onClick={closeDetails} aria-label="بستن"><FiX/></button></header><div className="ef-feedback-drawer-body"><FeedbackDetails feedback={selectedFeedback}/></div></aside></div>}
    </div>;
}

